import { FORMATO_UPLINK, type WsAudioGeneradoPorAlumnoPayload } from '@alter/types';
import { AudioRecorderError } from './errors.js';
import {
  defaultIsNativeOpusSupported,
  defaultNativeStrategyFactory,
  defaultPolyfillStrategyFactory,
} from './recorderStrategies.js';

/**
 * Metadata de `audio.generado_por_alumno` que produce el Recorder
 * (JC §2; nota de interfaz §3.1). `simulacion_id` y `secuencia` los
 * compone el caller antes de emitir: el Recorder no conoce la sesión.
 *
 * - `audio_ref_id`: UUID que correlaciona el JSON con el binario.
 * - `duracion_audio_segundos`: **medida** (wall-clock entre `start()` y
 *   `stop()`); alimenta el filtro de audios cortos del STT.
 * - `silencio_previo_segundos`: `0` mientras el cliente no mida silencio.
 * - `formato`: `webm/opus`. `muestra_hz` no se envía: el servidor no lo lee.
 * - `mime_type`: el `type` real del blob grabado.
 */
export type AudioRecorderMetadata = Omit<
  WsAudioGeneradoPorAlumnoPayload,
  'evento' | 'simulacion_id' | 'secuencia'
>;

/** Base de un MIME type, sin parámetros: `audio/webm;codecs=opus` → `audio/webm`. */
function mimeBase(mimeType: string): string {
  return (mimeType.split(';')[0] ?? '').trim().toLowerCase();
}

/**
 * ¿Puede el Recorder producir el `uplink.mime_type` que el servidor
 * declara en el Paso 2? La ruta nativa (`MediaRecorder`) produce
 * `audio/webm` con Opus; la ruta polyfill (Safari) produce Ogg y no cumple
 * una declaración `audio/webm`.
 */
export function puedeGrabarMimeType(
  mimeType: string,
  isNativeOpusSupported: () => boolean = defaultIsNativeOpusSupported,
): boolean {
  return mimeBase(mimeType) === 'audio/webm' && isNativeOpusSupported();
}

export interface AudioRecorderResult {
  metadata: AudioRecorderMetadata;
  /** Audio webm/opus (Chrome/Firefox/Edge) u ogg/opus (Safari vía polyfill). */
  binary: ArrayBuffer;
}

/**
 * Estrategia interna que abstrae la diferencia entre `MediaRecorder`
 * nativo (Chrome/Firefox) y el polyfill WASM `opus-recorder` (Safari).
 * El `AudioRecorder` público elige una de las dos en `start()`.
 */
export interface RecorderStrategy {
  start(): Promise<void>;
  /** `mimeType`: el `type` real del blob grabado. */
  stop(): Promise<{ binary: ArrayBuffer; mimeType: string }>;
  dispose(): void;
}

export interface AudioRecorderConfig {
  /**
   * MediaStream del micrófono. **El caller hace `getUserMedia`**, no el
   * Recorder. Razón: en Safari iOS, el primer `getUserMedia` debe ocurrir
   * dentro del handler del gesto de usuario (tap), una decisión de UX
   * que vive en el componente React de F1 (`04_ARQUITECTURA.md` §4.3).
   */
  stream: MediaStream;

  /**
   * Detección runtime del soporte nativo. Default:
   * `MediaRecorder.isTypeSupported('audio/webm;codecs=opus')`. Inyectable
   * para tests y para forzar la ruta polyfill en debug.
   */
  isNativeOpusSupported?: () => boolean;

  /**
   * Factoría de la estrategia nativa (MediaRecorder + webm/opus). Default:
   * `defaultNativeStrategyFactory`. Inyectable para tests.
   */
  nativeStrategyFactory?: (stream: MediaStream) => RecorderStrategy;

  /**
   * Factoría de la estrategia polyfill (Safari → `opus-recorder` WASM).
   * Default: `defaultPolyfillStrategyFactory` (lazy `import('opus-recorder')`).
   * Inyectable para tests y para reemplazar el polyfill sin tocar este
   * archivo.
   */
  polyfillStrategyFactory?: (stream: MediaStream) => Promise<RecorderStrategy>;

  /** Reloj wall-clock (default `performance.now`). */
  now?: () => number;

  /** Generador de UUID (default `crypto.randomUUID`). */
  randomUUID?: () => string;
}

/**
 * Captura de audio del alumno en webm/opus (o ogg/opus en Safari). API
 * uniforme para el caller — la elección de ruta nativa vs polyfill es
 * interna. Los errores son tipados (`AudioRecorderError` con `code`
 * discriminado), sin recuperación silenciosa.
 *
 * Patrón de uso:
 *
 * ```ts
 * const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
 * const rec = new AudioRecorder({ stream });
 * await rec.start();
 * // … alumno habla …
 * const { metadata, binary } = await rec.stop();
 * ws.emitAudioGeneradoPorAlumno(
 *   { ...metadata, simulacion_id, secuencia },
 *   binary,
 * );
 * rec.dispose();
 * ```
 */
export class AudioRecorder {
  private readonly stream: MediaStream;
  private readonly isNativeOpusSupported: () => boolean;
  private readonly nativeStrategyFactory: (stream: MediaStream) => RecorderStrategy;
  private readonly polyfillStrategyFactory: (stream: MediaStream) => Promise<RecorderStrategy>;
  private readonly now: () => number;
  private readonly randomUUID: () => string;

  private strategy: RecorderStrategy | null = null;
  private startedAt: number | null = null;
  private audioRefId: string | null = null;

  constructor(config: AudioRecorderConfig) {
    this.stream = config.stream;
    this.isNativeOpusSupported = config.isNativeOpusSupported ?? defaultIsNativeOpusSupported;
    this.nativeStrategyFactory = config.nativeStrategyFactory ?? defaultNativeStrategyFactory;
    this.polyfillStrategyFactory = config.polyfillStrategyFactory ?? defaultPolyfillStrategyFactory;
    this.now = config.now ?? defaultNow;
    this.randomUUID = config.randomUUID ?? defaultRandomUUID;
  }

  async start(): Promise<void> {
    if (this.strategy !== null) {
      throw new AudioRecorderError(
        'stream_inactive',
        'AudioRecorder.start() called while a recording is already in progress',
      );
    }
    if (!isStreamActive(this.stream)) {
      throw new AudioRecorderError('stream_inactive', 'MediaStream has no active audio track');
    }

    let strategy: RecorderStrategy;
    if (this.isNativeOpusSupported()) {
      strategy = this.nativeStrategyFactory(this.stream);
    } else {
      try {
        strategy = await this.polyfillStrategyFactory(this.stream);
      } catch (cause) {
        throw new AudioRecorderError(
          'polyfill_load_failed',
          'Failed to load opus-recorder polyfill for Safari',
          cause,
        );
      }
    }

    try {
      await strategy.start();
    } catch (cause) {
      strategy.dispose();
      throw new AudioRecorderError('encoding_failed', 'Recorder failed to start', cause);
    }

    this.strategy = strategy;
    this.audioRefId = this.randomUUID();
    this.startedAt = this.now();
  }

  async stop(): Promise<AudioRecorderResult> {
    const strategy = this.strategy;
    const startedAt = this.startedAt;
    const audioRefId = this.audioRefId;
    if (strategy === null || startedAt === null || audioRefId === null) {
      throw new AudioRecorderError('stream_inactive', 'AudioRecorder.stop() called before start()');
    }

    let result: { binary: ArrayBuffer; mimeType: string };
    try {
      result = await strategy.stop();
    } catch (cause) {
      strategy.dispose();
      this.resetState();
      throw new AudioRecorderError(
        'encoding_failed',
        'Recorder failed to flush final chunk',
        cause,
      );
    }

    const duracion_audio_segundos = (this.now() - startedAt) / 1000;
    strategy.dispose();
    this.resetState();

    return {
      metadata: {
        audio_ref_id: audioRefId,
        duracion_audio_segundos,
        silencio_previo_segundos: 0,
        formato: FORMATO_UPLINK,
        mime_type: result.mimeType,
      },
      binary: result.binary,
    };
  }

  /**
   * Libera recursos. Idempotente: llamar tras `stop()` o sin haber llamado
   * a `start()` no rompe. **No** detiene los tracks del `MediaStream` —
   * eso es responsabilidad del caller que llamó a `getUserMedia`.
   */
  dispose(): void {
    if (this.strategy !== null) {
      this.strategy.dispose();
    }
    this.resetState();
  }

  private resetState(): void {
    this.strategy = null;
    this.startedAt = null;
    this.audioRefId = null;
  }
}

function isStreamActive(stream: MediaStream): boolean {
  if (!stream.active) return false;
  const tracks = stream.getAudioTracks();
  if (tracks.length === 0) return false;
  return tracks.some((t) => t.readyState === 'live');
}

function defaultNow(): number {
  return performance.now();
}

function defaultRandomUUID(): string {
  return crypto.randomUUID();
}
